import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { SlotController } from '../common/controllers/SlotController.js'

import componentStyle from '../common/styles/Component.css'
import style from './TagGroup.css'

/**
 * Tag groups are designed to bring together selectable tags that are of a similar nature. For example categories you can filter by.
 *
 * @status new
 * @category structure
 * @slot - The tag group content
 *
 * @fires {NordEvent} change - Fired whenever a tag has been checked or unchecked via user interaction.
 * @fires {NordEvent} remove - Fired when the remove button is activated on a tag. This event should be used to remove the tag from the DOM.
 *
 * @cssprop [--n-tag-group-border-radius=var(--n-border-radius-s)] - Controls the rounded corners of the tag group, using [border radius tokens](/tokens/#border-radius). Only relevant for the default variant.
 * @cssprop [--n-tag-group-box-shadow=var(--n-box-shadow)] - Controls the surrounding shadow, using [box shadow tokens](/tokens/#box-shadow). Only relevant for the default variant.
 */
@customElement('nord-tag-group')
export default class TagGroup extends LitElement {
  static styles = [componentStyle, style]

  private defaultSlot = new SlotController(this)
  private dirController = new DirectionController(this)

  /**
   * The style variant of the tag group.
   */
  @property({ reflect: true }) variant: 'default' | 'spaced' = 'default'

  /**
   * The direction of the tag group.
   */
  @property({ reflect: true }) direction: 'vertical' | 'horizontal' = 'horizontal'

  /**
   * Defines whether the tags are forced in a single line
   * or can be flowed into multiple lines (only applied when variant is set to `spaced`).
   */
  @property({ reflect: true, type: Boolean }) wrap = false

  /**
   * The appropriate role for the containing element.
   */

  @property({ reflect: true }) role: string = 'group'

  render() {
    const hasMultipleTags = this.defaultSlot.assigned.length > 1

    return html`<div
      class=${classMap({
        'n-tag-group': true,
        'n-rtl': this.dirController.isRTL,
        'n-multiple-tags': hasMultipleTags,
      })}
    >
      <slot></slot>
    </div>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-tag-group': TagGroup
  }
}
